import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DocumentContentSkeleton } from "@/components/skeletons";
import { sanitizeHtml } from "@/lib/sanitize";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { RESPONSIVE } from "@/design-system";
import { useInstitutionalPageBySlug } from "@/hooks/useInstitutionalPageBySlug";
import { LCommon } from "@/components/i18n";

interface LegalDocumentModalProps {
  slug: "terms-of-use" | "privacy-policy";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LegalDocumentModal({ slug, open, onOpenChange }: LegalDocumentModalProps) {
  const { page, isLoading } = useInstitutionalPageBySlug(slug);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1rem)] sm:w-[calc(100%-2rem)] max-w-4xl max-h-[90vh] sm:max-h-[85vh] p-0 overflow-hidden">
        {/* Screen-reader title (Radix requires one); visually hidden */}
        <DialogTitle className="sr-only">{page?.title ?? slug}</DialogTitle>
        <DialogClose className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus-visible:outline-none focus-visible:border-ring">
          <X className="h-5 w-5" />
          <span className="sr-only"><LCommon id="legal_document_modal.close" defaultValue="Close" /></span>
        </DialogClose>
        <ScrollArea className="max-h-[calc(90vh-64px)] sm:max-h-[calc(85vh-64px)] mt-10" scrollbarClassName="py-3">
          <div className={cn(RESPONSIVE.paddingCardContentWide, "pr-4 pb-6")}>
            {isLoading ? (
              <DocumentContentSkeleton lineCount={5} />
            ) : page?.content_html ? (
              <div 
                className="prose prose-sm dark:prose-invert max-w-none
                  prose-headings:text-foreground prose-headings:font-semibold
                  prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                  prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3
                  prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2
                  prose-p:text-muted-foreground prose-p:leading-relaxed
                  prose-li:text-muted-foreground
                  prose-strong:text-foreground prose-strong:font-semibold
                  prose-ul:my-2 prose-ol:my-2
                  prose-li:my-1"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content_html) }}
              />
            ) : (
              <p className="text-muted-foreground"><LCommon id="legal_document_modal.not_found" defaultValue="Document not found" /></p>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
